import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, Briefcase, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/shared/utilities/cn';
import { useTenant } from '@/shared/tenant';
import { Loading } from '@/shared/components/feedback';

export function TenantSelector() {
  const { t } = useTranslation();
  const {
    organization,
    client,
    workspace,
    organizations,
    clients,
    workspaces,
    isLoadingOrganizations,
    isLoadingClients,
    isLoadingWorkspaces,
    selectOrganization,
    selectClient,
    selectWorkspace,
  } = useTenant();

  const [activeDropdown, setActiveDropdown] = useState<'org' | 'client' | 'workspace' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOrganization = (id: string) => {
    selectOrganization(id);
    setActiveDropdown(null);
  };

  const handleSelectClient = (id: string) => {
    selectClient(id);
    setActiveDropdown(null);
  };

  const handleSelectWorkspace = (id: string) => {
    selectWorkspace(id);
    setActiveDropdown(null);
  };

  return (
    <div ref={dropdownRef} className="flex items-center gap-1">
      {/* Organization selector */}
      <SelectorButton
        icon={<Building2 className="h-4 w-4" />}
        label={organization?.name || t('tenant.selectOrganization')}
        isLoading={isLoadingOrganizations}
        isActive={activeDropdown === 'org'}
        onClick={() => setActiveDropdown(activeDropdown === 'org' ? null : 'org')}
        hasValue={!!organization}
      />
      {activeDropdown === 'org' && (
        <SelectorDropdown
          items={organizations.map((o) => ({
            id: o.id,
            label: o.name,
            selected: o.id === organization?.id,
          }))}
          onSelect={handleSelectOrganization}
          emptyMessage={t('tenant.noOrganizations')}
        />
      )}

      {/* Separator */}
      {organization && (
        <span className="mx-1 text-slate-300 dark:text-slate-600">/</span>
      )}

      {/* Client selector */}
      {organization && (
        <>
          <SelectorButton
            icon={<Users className="h-4 w-4" />}
            label={client?.name || t('tenant.selectClient')}
            isLoading={isLoadingClients}
            isActive={activeDropdown === 'client'}
            onClick={() => setActiveDropdown(activeDropdown === 'client' ? null : 'client')}
            hasValue={!!client}
          />
          {activeDropdown === 'client' && (
            <SelectorDropdown
              items={clients.map((c) => ({
                id: c.id,
                label: c.name,
                selected: c.id === client?.id,
              }))}
              onSelect={handleSelectClient}
              emptyMessage={t('tenant.noClients')}
            />
          )}
        </>
      )}

      {/* Separator */}
      {client && <span className="mx-1 text-slate-300 dark:text-slate-600">/</span>}

      {/* Workspace selector */}
      {client && (
        <>
          <SelectorButton
            icon={<Briefcase className="h-4 w-4" />}
            label={workspace?.name || t('tenant.selectWorkspace')}
            isLoading={isLoadingWorkspaces}
            isActive={activeDropdown === 'workspace'}
            onClick={() => setActiveDropdown(activeDropdown === 'workspace' ? null : 'workspace')}
            hasValue={!!workspace}
          />
          {activeDropdown === 'workspace' && (
            <SelectorDropdown
              items={workspaces.map((w) => ({
                id: w.id,
                label: w.name,
                sublabel: w.environment,
                selected: w.id === workspace?.id,
              }))}
              onSelect={handleSelectWorkspace}
              emptyMessage={t('tenant.noWorkspaces')}
            />
          )}
        </>
      )}
    </div>
  );
}

interface SelectorButtonProps {
  icon: React.ReactNode;
  label: string;
  isLoading: boolean;
  isActive: boolean;
  onClick: () => void;
  hasValue: boolean;
}

function SelectorButton({
  icon,
  label,
  isLoading,
  isActive,
  onClick,
  hasValue,
}: SelectorButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
        'hover:bg-slate-100 dark:hover:bg-slate-800',
        isActive && 'bg-slate-100 dark:bg-slate-800',
        hasValue
          ? 'text-slate-900 dark:text-slate-100'
          : 'text-slate-500 dark:text-slate-400'
      )}
    >
      {isLoading ? (
        <Loading size="sm" />
      ) : (
        <>
          {icon}
          <span className="hidden max-w-[120px] truncate sm:inline">{label}</span>
          <ChevronDown className="h-3 w-3 text-slate-400" />
        </>
      )}
    </button>
  );
}

interface SelectorDropdownProps {
  items: Array<{
    id: string;
    label: string;
    sublabel?: string;
    selected: boolean;
  }>;
  onSelect: (id: string) => void;
  emptyMessage: string;
}

function SelectorDropdown({ items, onSelect, emptyMessage }: SelectorDropdownProps) {
  return (
    <div className="absolute top-full mt-1 w-64 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
      {items.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-500">{emptyMessage}</p>
      ) : (
        <ul className="max-h-64 overflow-auto">
          {items.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => onSelect(item.id)}
                className={cn(
                  'flex w-full items-center gap-2 px-4 py-2 text-start text-sm',
                  'hover:bg-slate-100 dark:hover:bg-slate-700',
                  item.selected && 'bg-primary-50 dark:bg-primary-900/20'
                )}
              >
                <span className="flex-1">
                  <span className="block font-medium text-slate-900 dark:text-slate-100">
                    {item.label}
                  </span>
                  {item.sublabel && (
                    <span className="text-xs text-slate-500">{item.sublabel}</span>
                  )}
                </span>
                {item.selected && (
                  <Check className="h-4 w-4 text-primary-600" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
